import { useCallback, useMemo } from 'react'
import moment from 'moment-timezone'
import { toLower } from 'ramda'

import { PlainItemsSelector } from 'app/common/ui-next/plain-selector'
import * as dm from 'app/dataTypes/dayModes'
import * as dmt from 'app/dataTypes/DayModes.types'
import { getInputTime, PlainInputTime } from 'app/common/ui-next/plain-input-time'
import PlainLabel from 'app/common/ui-next/plain-label'

import styles from './TimeRestrictionsFoodMode.scss'

const dayModes = {
  [dm.TODAY]: { id: dm.TODAY, title: 'В день питания' },
  [dm.BEFORE]: { id: dm.BEFORE, title: 'За день до питания' },
  [dm.NEXT_WEEK]: { id: dm.NEXT_WEEK, title: 'Питание на след. неделю' },
}

const foodModeTitles = ['Завтрак', 'Обед', 'Полдник']

type Props = {
  disabled?: boolean
  fmNumber: number
  groupNumber: number
  time: string
  dayMode: dmt.Before | dmt.Today | dmt.NextWeek
  wday: number | null
  onUpdateTime: (gNum: number, fmNum: number, value: string) => void
  onUpdateWeekday: (gNum: number, fmNum: number, value: number | null) => void
  onUpdateDayMode: (
    gNum: number,
    fmNum: number,
    value: dmt.Before | dmt.Today | dmt.NextWeek,
  ) => void
}

const TimeRestrictionsFoodMode = ({
  disabled,
  fmNumber,
  groupNumber,
  time,
  dayMode,
  wday,
  onUpdateTime,
  onUpdateWeekday,
  onUpdateDayMode,
}: Props) => {
  const handleDayModeChange = useCallback(
    (value: Array<string>) => {
      if (value[0] === dm.NEXT_WEEK || value[0] === dm.BEFORE || value[0] === dm.TODAY) {
        onUpdateDayMode(groupNumber, fmNumber, value[0])
      }
    },
    [groupNumber, fmNumber, onUpdateDayMode],
  )

  const handleChangeTime = useCallback(
    (value: string) => {
      const { weekday, time } = getInputTime(value)
      if (time === null) return
      onUpdateTime(groupNumber, fmNumber, time)

      if (dayMode === dm.NEXT_WEEK) {
        const weekdays = moment.weekdays().map(toLower)
        const weekdayIndex = weekday !== null ? weekdays.indexOf(toLower(weekday)) : -1
        if (weekday) {
          onUpdateWeekday(groupNumber, fmNumber, weekdayIndex === -1 ? null : weekdayIndex)
        }
      }
    },
    [groupNumber, fmNumber, onUpdateTime, onUpdateWeekday, dayMode],
  )

  const unputValue = useMemo(() => {
    if (dayMode === dm.NEXT_WEEK && wday !== null) {
      const weekdays = moment.weekdays() // локализованные дни недели, без true возвращает начиная с воскресенья
      return `${weekdays[wday]} ${time}`
    }
    return time
  }, [dayMode, wday, time])

  return (
    <div className={styles.root}>
      <div>
        <PlainLabel htmlFor="time" displayAsBlock>
          {foodModeTitles[fmNumber]}
        </PlainLabel>
        <PlainInputTime
          id="time"
          value={unputValue}
          onValue={handleChangeTime}
          weekdaysShort={dayMode === dm.NEXT_WEEK}
        />
      </div>

      <PlainItemsSelector
        label="Ограничить"
        header="Выберите ограничение"
        buttonResetLabel=""
        disabled={disabled}
        selectedAllOnEmptySelection={false}
        items={dayModes}
        selection={[dayMode]}
        onChange={handleDayModeChange}
      />
    </div>
  )
}

export default TimeRestrictionsFoodMode
